import { useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, Send, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { createLead } from "@/lib/api";
import { Eyebrow } from "@/components/landing/shared";
import { useSiteContent } from "@/context/ContentContext";

const TEAM_SIZES = ["1–5", "5–10", "11–25", "26–50", "51–100", "100+"];
const PROJECT_TYPES = [
  "Sito / Landing page",
  "Web App / Gestionale",
  "E-commerce",
  "Software su misura",
  "Integrazioni / Automazioni",
  "Altro",
];
const BUDGETS = [
  "€5.000–10.000",
  "€10.000–20.000",
  "€20.000–50.000",
  "€50.000+",
  "Da definire",
];
const TIMELINES = [
  "Il prima possibile",
  "Entro 3 mesi",
  "Entro 6 mesi",
  "Sto ancora valutando",
];

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const emptyForm = {
  first_name: "",
  last_name: "",
  company: "",
  email: "",
  phone: "",
  sector: "",
  team_size: "",
  current_process: "",
  main_problem: "",
  automation_goal: "",
  project_type: "",
  budget_range: "",
  timeline: "",
};

const inputCls =
  "h-11 rounded-lg border-black/10 bg-black/[0.03] text-[color:var(--text-primary)] placeholder:text-black/40 focus-visible:ring-2 focus-visible:ring-emerald-300/50 focus-visible:ring-offset-0";
const areaCls =
  "rounded-lg border-black/10 bg-black/[0.03] text-[color:var(--text-primary)] placeholder:text-black/40 focus-visible:ring-2 focus-visible:ring-emerald-300/50 focus-visible:ring-offset-0";
const selectContentCls =
  "border-black/10 bg-[var(--ink-850)] text-[color:var(--text-primary)]";

export default function ContactSection() {
  const { contact } = useSiteContent();
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const set = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.first_name.trim()) e.first_name = "Inserisci il tuo nome.";
    if (!form.last_name.trim()) e.last_name = "Inserisci il tuo cognome.";
    if (!form.company.trim()) e.company = "Inserisci il nome dell'azienda.";
    if (!EMAIL_RE.test(form.email.trim())) e.email = "Email non valida.";
    if (!form.sector.trim()) e.sector = "Indica il settore.";
    if (!form.team_size) e.team_size = "Seleziona un valore.";
    if (!form.current_process.trim() || form.current_process.trim().length < 5)
      e.current_process = "Raccontami come gestite oggi il processo.";
    if (!form.main_problem.trim() || form.main_problem.trim().length < 5)
      e.main_problem = "Qual è il problema principale?";
    if (!form.project_type) e.project_type = "Seleziona il tipo di progetto.";
    if (!form.budget_range) e.budget_range = "Seleziona un budget.";
    if (!form.timeline) e.timeline = "Seleziona le tempistiche.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (submitting) return;
    if (!validate()) {
      toast.error("Controlla i campi evidenziati.");
      return;
    }
    setSubmitting(true);
    try {
      await createLead({ ...form, source: "landing" });
      setSuccess(true);
      setForm(emptyForm);
      toast.success("Progetto inviato correttamente.");
    } catch (err) {
      const detail =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Si è verificato un errore. Riprova tra poco.";
      toast.error(typeof detail === "string" ? detail : "Errore di invio.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contatto"
      className="relative scroll-mt-20 border-t border-black/[0.08] py-16 md:py-24 lg:py-28"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-[220px]"
        style={{
          background:
            "radial-gradient(closest-side, rgba(16,185,129,0.10), transparent 72%)",
        }}
      />
      <div className="relative mx-auto grid w-full max-w-6xl grid-cols-1 gap-12 px-5 sm:px-6 lg:grid-cols-12 lg:gap-16 lg:px-8">
        {/* Left copy */}
        <div className="lg:col-span-5">
          <Eyebrow>{contact.eyebrow}</Eyebrow>
          <h2 className="mt-4 text-2xl font-semibold leading-tight tracking-[-0.03em] text-[color:var(--text-primary)] sm:text-3xl lg:text-4xl">
            {contact.heading}
          </h2>
          <p className="mt-5 max-w-md text-base leading-relaxed text-secondary">
            {contact.subheading}
          </p>

          <ul className="mt-8 space-y-4">
            {(contact.benefits || []).map((t, i) => (
              <li
                key={`${t}-${i}`}
                className="flex items-start gap-3 text-sm text-secondary"
              >
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                {t}
              </li>
            ))}
          </ul>

          <div className="mt-8 flex items-center gap-2 rounded-lg border border-black/[0.08] bg-black/[0.02] px-3 py-2.5 text-xs text-tertiary">
            <ShieldCheck className="h-4 w-4 text-emerald-600" />
            {contact.privacy_note}
          </div>
        </div>

        {/* Right form / success */}
        <div className="lg:col-span-7">
          <div className="rounded-[18px] border border-black/10 bg-white p-6 sm:p-8">
            {success ? (
              <motion.div
                data-testid="lead-form-success-message"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                className="flex flex-col items-center py-10 text-center"
              >
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-emerald-600/15">
                  <CheckCircle2 className="h-7 w-7 text-emerald-600" />
                </span>
                <h3 className="mt-5 text-xl font-semibold text-[color:var(--text-primary)]">
                  {contact.success_title}
                </h3>
                <p className="mt-2 max-w-sm text-sm text-secondary">
                  {contact.success_body}
                </p>
                <Button
                  data-testid="lead-form-reset-button"
                  variant="outline"
                  onClick={() => setSuccess(false)}
                  className="mt-6 h-10 rounded-lg border-black/15 bg-transparent text-[color:var(--text-primary)] hover:border-black/20 hover:bg-black/[0.04]"
                >
                  Invia un altro progetto
                </Button>
              </motion.div>
            ) : (
              <form
                data-testid="lead-form"
                onSubmit={onSubmit}
                noValidate
                className="space-y-5"
              >
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <Field label="Nome" error={errors.first_name}>
                    <Input
                      data-testid="lead-form-firstname-input"
                      value={form.first_name}
                      onChange={(e) => set("first_name", e.target.value)}
                      placeholder="Mario"
                      className={inputCls}
                    />
                  </Field>
                  <Field label="Cognome" error={errors.last_name}>
                    <Input
                      data-testid="lead-form-lastname-input"
                      value={form.last_name}
                      onChange={(e) => set("last_name", e.target.value)}
                      placeholder="Rossi"
                      className={inputCls}
                    />
                  </Field>
                </div>

                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <Field label="Azienda" error={errors.company}>
                    <Input
                      data-testid="lead-form-company-input"
                      value={form.company}
                      onChange={(e) => set("company", e.target.value)}
                      placeholder="Rossi SRL"
                      className={inputCls}
                    />
                  </Field>
                  <Field label="Email" error={errors.email}>
                    <Input
                      data-testid="lead-form-email-input"
                      type="email"
                      value={form.email}
                      onChange={(e) => set("email", e.target.value)}
                      placeholder="mario@azienda.it"
                      className={inputCls}
                    />
                  </Field>
                </div>

                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <Field label="Telefono (opzionale)">
                    <Input
                      data-testid="lead-form-phone-input"
                      type="tel"
                      value={form.phone}
                      onChange={(e) => set("phone", e.target.value)}
                      placeholder="+39 ..."
                      className={inputCls}
                    />
                  </Field>
                  <Field label="Settore" error={errors.sector}>
                    <Input
                      data-testid="lead-form-industry-input"
                      value={form.sector}
                      onChange={(e) => set("sector", e.target.value)}
                      placeholder="Es. logistica, retail, produzione..."
                      className={inputCls}
                    />
                  </Field>
                </div>

                <Field
                  label="Numero di dipendenti / utenti"
                  error={errors.team_size}
                >
                  <Select
                    value={form.team_size}
                    onValueChange={(v) => set("team_size", v)}
                  >
                    <SelectTrigger
                      data-testid="lead-form-teamsize-select"
                      aria-label="Numero di dipendenti o utenti"
                      className={inputCls}
                    >
                      <SelectValue placeholder="Seleziona" />
                    </SelectTrigger>
                    <SelectContent className={selectContentCls}>
                      {TEAM_SIZES.map((s) => (
                        <SelectItem key={s} value={s}>
                          {s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>

                <Field
                  label="Come gestite oggi il processo?"
                  error={errors.current_process}
                >
                  <Textarea
                    data-testid="lead-form-process-textarea"
                    value={form.current_process}
                    onChange={(e) => set("current_process", e.target.value)}
                    rows={3}
                    placeholder="Strumenti attuali, passaggi manuali, dove nascono i dati..."
                    className={areaCls}
                  />
                </Field>

                <Field
                  label="Qual è il problema principale?"
                  error={errors.main_problem}
                >
                  <Textarea
                    data-testid="lead-form-problem-textarea"
                    value={form.main_problem}
                    onChange={(e) => set("main_problem", e.target.value)}
                    rows={3}
                    placeholder="Cosa oggi rallenta il lavoro o crea errori?"
                    className={areaCls}
                  />
                </Field>

                <Field label="Cosa vorreste automatizzare? (opzionale)">
                  <Textarea
                    data-testid="lead-form-automation-textarea"
                    value={form.automation_goal}
                    onChange={(e) => set("automation_goal", e.target.value)}
                    rows={2}
                    placeholder="Attività ripetitive, report, notifiche, inserimenti doppi..."
                    className={areaCls}
                  />
                </Field>

                <Field label="Tipo di progetto" error={errors.project_type}>
                  <Select
                    value={form.project_type}
                    onValueChange={(v) => set("project_type", v)}
                  >
                    <SelectTrigger
                      data-testid="lead-form-projecttype-select"
                      aria-label="Tipo di progetto"
                      className={inputCls}
                    >
                      <SelectValue placeholder="Seleziona" />
                    </SelectTrigger>
                    <SelectContent className={selectContentCls}>
                      {PROJECT_TYPES.map((t) => (
                        <SelectItem key={t} value={t}>
                          {t}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>

                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <Field label="Budget indicativo" error={errors.budget_range}>
                    <Select
                      value={form.budget_range}
                      onValueChange={(v) => set("budget_range", v)}
                    >
                      <SelectTrigger
                        data-testid="lead-form-budget-select"
                        aria-label="Budget indicativo"
                        className={inputCls}
                      >
                        <SelectValue placeholder="Seleziona" />
                      </SelectTrigger>
                      <SelectContent className={selectContentCls}>
                        {BUDGETS.map((b) => (
                          <SelectItem key={b} value={b}>
                            {b}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>

                  <Field label="Tempistiche" error={errors.timeline}>
                    <Select
                      value={form.timeline}
                      onValueChange={(v) => set("timeline", v)}
                    >
                      <SelectTrigger
                        data-testid="lead-form-timeline-select"
                        aria-label="Tempistiche"
                        className={inputCls}
                      >
                        <SelectValue placeholder="Seleziona" />
                      </SelectTrigger>
                      <SelectContent className={selectContentCls}>
                        {TIMELINES.map((t) => (
                          <SelectItem key={t} value={t}>
                            {t}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                </div>

                <p className="flex items-center justify-center gap-1.5 text-center text-xs font-medium text-[color:var(--gold-600)]">
                  <span className="inline-block h-1.5 w-1.5 rounded-full bg-[color:var(--gold-500)]" />
                  Posti consulenza limitati questo mese · max 4 progetti l'anno
                </p>
                <Button
                  data-testid="lead-form-submit-button"
                  type="submit"
                  disabled={submitting}
                  className="h-12 w-full rounded-xl bg-emerald-700 text-[15px] font-medium text-white transition-colors duration-200 hover:bg-emerald-600 disabled:opacity-70"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Invio in corso…
                    </>
                  ) : (
                    <>
                      {contact.submit_cta}
                      <Send className="ml-2 h-4 w-4" />
                    </>
                  )}
                </Button>
                <p className="text-center text-xs text-tertiary">
                  {contact.response_note}
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, error, children }) {
  return (
    <div className="space-y-2">
      <Label className="text-sm text-secondary">{label}</Label>
      {children}
      {error ? (
        <p className="text-xs text-red-400" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
